// ---------------------------------------------------------------------------
// Evaluating a round's result from one side — the search bot's yardstick
// ---------------------------------------------------------------------------
//
// evaluate(result, seat, weights) → a number, higher is better for `seat`.
// `result` is resolveRound's output on a SAMPLED opponent Grid, so every term
// is an expectation once averaged over the samples.
//
// Defaults (DEFAULT_WEIGHTS) and why:
//   win 10000        ending the war outweighs everything else
//   structure 100    the war is scored in structures: one kill (or one loss
//                    avoided) is the unit everything else trades against
//   drone 2          a drone is worth a fiftieth of a structure. Tuned: a
//                    one-round search that prizes drones (8 was the first
//                    guess) holds them back, but drones only pay by winning
//                    clashes, which spends them — fast vs random went 85.8%
//                    (drone 8) → 90.0% (2) over 1000 wars each
//   broodMature 16   per live drone on a mature brood: drones that don't need
//                    deploying, plus the airdrop egg every round (16/10 over
//                    10/6: 90.0% → 91.9%)
//   broodBuilding 25 a construction: two drones paid, not yet fighting (plus
//                    the egg it promises, below)
//   egg 40           a brood whose egg can still fly: an airdrop every round
//                    from now on — the engine of the war a human won 3-0 by
//                    brooding early and keeping the broods safe, which a
//                    one-round search never sees. Against a scripted opponent
//                    playing that war (builderBot's brooder) the fast bot
//                    went 11.8% -> 96.5% (400 wars; egg 20: 65.5%, 60: 97.3%),
//                    builder 100%, random 99.3% (was ~88%). It counts for the
//                    opponent too, so their broods are worth killing
//   rocket 10        a standing rocket: worth a little more than the 200
//                    yield it cost (8 at yieldPoint 0.04), because it saves
//                    structures in rounds the one-round search doesn't see
//   yieldPoint 0.04  yield in hand, so spending needs a reason
//   intelCell 0.5    each opponent cell whose contents the player now knows
//   intelStamp 2     each live intel stamp the player holds
//   edge 5           per drone of built-up advantage beyond the first, in each
//                    corridor where one side outnumbers the other by 2+ after
//                    the round (theirs counts against): a one-round search
//                    otherwise never builds a stack, only spends one, and
//                    can't break a defender who reinforces. Against a
//                    scripted stacking, reinforcing opponent (builderBot) the
//                    fast bot's win rate went 8.7% -> 38.3% (300 wars; edge 3:
//                    28.3%, 8: 41.7%), with no loss against itself (48.3% vs
//                    the edge-0 bot) or random (90.5%); 8 cost too much there
//   threat 0         strike potential: per corridor, the best crater our
//                    post-round edge there could aim next round
//                    (candidates.threatTable). Off: at 10 / 25 / 50 the fast
//                    bot fell to 87.3% / 84.3% / 75.5% vs the brooder (96.3%
//                    without, 400 wars each) and its strikes killed no more
//                    often — a one-round search can't tell a stack that will
//                    get to strike from one the defender simply meets
//   washedAttack 0   a penalty per own attack that ends level (both corridors
//                    cleared). Off: tried at 10 / 25 / 50, the fast bot then
//                    won 46.5% / 44.6% / 47.3% of 1000 wars against itself
//                    unpenalised — holding back from attacks that might wash
//                    also gives up attacks that would have won
// Opponent terms mirror the own ones where the result shows them (their
// drones, broods, kills); their yield is never visible, so it isn't scored.

import { basesOf, calculateCombatStrength } from '../gameLogic.js'
import { viewOfOpponentGrid } from './view.js'

export const DEFAULT_WEIGHTS = Object.freeze({
    win: 10000, structure: 100, drone: 2, broodMature: 16, broodBuilding: 25,
    rocket: 10, yieldPoint: 0.04, intelCell: 0.5, intelStamp: 2,
    washedAttack: 0,
    edge: 5,
    egg: 40,
    threat: 0,
})

const drones = (board) => board.reduce((n, bf) => n + (bf.armyQty || 0) + (bf.newArmies || 0) + (bf.movedInArmies || 0), 0)
// A brood: its drones, and — while its egg can still fly — the airdrop it
// will deliver every round from now on (egg), which the one-round search
// otherwise never sees. A construction is the promise of both.
function broodValue(board, w) {
    let v = 0
    for (const bf of board) for (const b of basesOf(bf)) {
        if (!b.mature) v += w.broodBuilding + w.egg
        else v += (b.hp ?? 2) * w.broodMature + ((b.egg ?? 'egg') === 'egg' ? w.egg : 0)
    }
    return v
}
function rockets(hf) {
    let n = 0
    for (const st of Object.values(hf?.structures ?? {})) {
        n += (st.rocketCells || []).filter(s => !hf.subplots[s]?.destroyed).length
    }
    return n
}
function intel(oppHf, w) {
    const g = viewOfOpponentGrid(oppHf)
    let known = 0
    for (const c of g.cells) if (c.known !== 'unknown') known++
    const stamps = g.stamps.filter(t => !t.dead && t.distance != null).length
    return known * w.intelCell + stamps * w.intelStamp
}

export function evaluate(result, seat, weights = DEFAULT_WEIGHTS, context = null) {
    const w = weights
    const other = seat === 'p1' ? 'p2' : 'p1'
    const me = result[seat], opp = result[other]
    let v = 0
    if (result.outcome.over && !result.outcome.draw) v += result.outcome.winner === seat ? w.win : -w.win
    v += (opp.structuresLost - me.structuresLost) * w.structure
    v += (drones(me.board) - drones(opp.board)) * w.drone
    v += broodValue(me.board, w) - broodValue(opp.board, w)
    v += rockets(me.homefront) * w.rocket
    v += (me.yield ?? 0) * w.yieldPoint
    v += intel(opp.homefront, w)
    // An attack of ours that ends level: both corridors cleared, a round's
    // drones spent for nothing gained (events are in p1's terms).
    if (w.washedAttack) {
        for (const e of result.events) {
            if (e.type !== 'TIE') continue
            const mine = seat === 'p1' ? (e.context === 'self_attacked' || e.context === 'both_attacked')
                                       : (e.context === 'opp_attacked' || e.context === 'both_attacked')
            if (mine) v -= w.washedAttack
        }
    }
    // Built-up advantage: corridors where, after the round, one side
    // outnumbers the other by 2 or more — a stack that can land a real crater
    // next round. Ours count for us, theirs against. (The one-round search
    // otherwise sees no value in building a stack, only in spending it.)
    // Strike potential: per corridor, the best crater our post-round edge there
    // could aim next round (context.threat, candidates.threatTable).
    if (w.threat && context?.threat) {
        const full = (bf) => calculateCombatStrength(bf) + (bf.newArmies || 0)
        me.board.forEach((bf, c) => {
            const m = Math.min(5, full(bf) - full(opp.board[c]))
            if (m > 0) v += w.threat * context.threat[c][m]
        })
    }
    if (w.edge) {
        const full = (bf) => calculateCombatStrength(bf) + (bf.newArmies || 0)
        me.board.forEach((bf, c) => {
            const d = full(bf) - full(opp.board[c])
            if (d >= 2) v += w.edge * (d - 1)
            else if (d <= -2) v -= w.edge * (-d - 1)
        })
    }
    return v
}
